import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ShmHabilitationType,
  ConsultantService,
} from '@lib/services/consultant/consultant.service';
import { OuiBanner } from 'omnium-ui/banner';
import { OuiButton } from 'omnium-ui/button';
import { OuiDialogModule } from 'omnium-ui/dialog';
import { OuiLoading } from 'omnium-ui/loading';

export interface HabilitationInitiationDialogData {
  codeConsultant: string;
  type: ShmHabilitationType;
}

@Component({
  selector: 'lib-habilitation-initiation-dialog',
  imports: [OuiDialogModule, OuiButton, OuiBanner, OuiLoading],
  templateUrl: './habilitation-initiation-dialog.component.html',
  styleUrl: './habilitation-initiation-dialog.component.scss',
})
export class HabilitationInitiationDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<HabilitationInitiationDialogComponent>
  );
  private readonly data = inject<HabilitationInitiationDialogData>(MAT_DIALOG_DATA);
  private readonly consultantService = inject(ConsultantService);

  readonly type = this.data.type;
  readonly title = `Initier le parcours ${
    this.type === 'ASSURANCE' ? 'MIA' : this.type === 'FINANCEMENT' ? 'IOB' : 'Immobilier'
  }`;
  readonly eligibility = signal<Awaited<ReturnType<
    ConsultantService['getHabilitationInitiationEligibility']
  >> | null>(null);
  readonly blockedConditions = computed(
    () => this.eligibility()?.conditions.filter(condition => !condition.isSatisfied) ?? []
  );
  readonly isChecking = signal(true);
  readonly eligibilityError = signal<string | null>(null);
  readonly isSaving = signal(false);
  readonly error = signal<string | null>(null);
  readonly canConfirm = computed(
    () => !this.isChecking() && !this.isSaving() && this.eligibility()?.isEligible === true
  );

  ngOnInit(): void {
    void this.checkEligibility();
  }

  async checkEligibility(): Promise<void> {
    this.isChecking.set(true);
    this.eligibilityError.set(null);
    try {
      this.eligibility.set(
        await this.consultantService.getHabilitationInitiationEligibility(
          this.data.codeConsultant,
          this.type
        )
      );
    } catch (error) {
      this.eligibility.set(null);
      this.eligibilityError.set(
        error instanceof Error
          ? error.message
          : "Impossible de vérifier l'éligibilité du parcours."
      );
    } finally {
      this.isChecking.set(false);
    }
  }

  async confirm(): Promise<void> {
    if (!this.canConfirm()) return;
    this.isSaving.set(true);
    this.error.set(null);
    try {
      await this.consultantService.initierParcoursHabilitationParShm(
        this.data.codeConsultant,
        this.type
      );
      this.dialogRef.close(true);
    } catch (error) {
      this.error.set(
        error instanceof Error ? error.message : "L'initiation du parcours a échoué."
      );
      await this.refreshEligibility();
    } finally {
      this.isSaving.set(false);
    }
  }

  private async refreshEligibility(): Promise<void> {
    const mutationError = this.error();
    this.isChecking.set(true);
    try {
      this.eligibility.set(
        await this.consultantService.getHabilitationInitiationEligibility(
          this.data.codeConsultant,
          this.type
        )
      );
    } catch {
      this.eligibility.set(null);
    } finally {
      this.isChecking.set(false);
      this.error.set(mutationError);
    }
  }
}
